import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle } from 'lucide-react'
import axios from 'axios'
import logo from '@/assets/GrackleDocs_BirdOnly.png'
import { markFreshLogin } from '@/lib/loginSignal'

// Use relative URL for API calls - works in both local dev and production
const API_URL = '/api/v1'
const ADMIN_EMAIL = 'jmills@grackledocs.com'

export default function Login() {
  const [showAdminLogin, setShowAdminLogin] = useState(false)
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // If OAuth failed but user has existing valid tokens, redirect to dashboard
  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam === 'oauth_failed') {
      const hasTokens = localStorage.getItem('accessToken') && localStorage.getItem('user')
      if (hasTokens) {
        navigate('/', { replace: true })
        return
      }
    }
  }, [searchParams, navigate])

  // Check for error messages from OAuth redirect
  useEffect(() => {
    const errorParam = searchParams.get('error')
    const domainParam = searchParams.get('domain')
    const messageParam = searchParams.get('message')

    if (errorParam) {
      switch (errorParam) {
        case 'session_expired':
          setError('Your session has expired. Please log in again.')
          break
        case 'no_code':
          setError('OAuth authorization failed. Please try again.')
          break
        case 'invalid_domain':
          setError(
            `Invalid email domain: ${domainParam}. Only @grackledocs.com emails are allowed.`
          )
          break
        case 'account_inactive':
          setError('Your account has been deactivated. Please contact an administrator.')
          break
        case 'account_locked':
          setError('Your account is locked. Please contact an administrator.')
          break
        case 'oauth_failed':
          setError(`OAuth authentication failed: ${messageParam || 'Unknown error'}`)
          break
        default:
          setError('An error occurred during authentication.')
      }
    }
  }, [searchParams])

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: ADMIN_EMAIL,
        password,
      })

      const { user, accessToken, refreshToken } = response.data

      // Store tokens and user info
      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('user', JSON.stringify(user))
      localStorage.setItem('gracklecrm_current_user_id', user.id)
      // A real login, not a reload: lets the welcome-back summary open once we're in.
      markFreshLogin(user.id)

      // Navigate to dashboard
      navigate('/')
    } catch (err: any) {
      console.error('Login error:', err)

      if (err.response?.status === 429) {
        setError('Too many login attempts. Please try again later.')
      } else if (err.response?.data?.useOAuth) {
        setError('Please use Google OAuth to sign in.')
      } else if (err.response?.data?.attemptsRemaining !== undefined) {
        const remaining = err.response.data.attemptsRemaining
        setError(
          `Invalid credentials. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`
        )
      } else if (err.response?.data?.lockedUntil) {
        const until = new Date(err.response.data.lockedUntil).toLocaleTimeString()
        setError(`Account locked until ${until}.`)
      } else {
        setError(err.response?.data?.message || 'Login failed. Please check your credentials.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleOAuth = async () => {
    setError('')
    setIsGoogleLoading(true)

    try {
      // Get OAuth URL from backend
      const response = await axios.get(`${API_URL}/auth/google/url`)
      const { url } = response.data

      // Redirect to Google OAuth
      window.location.href = url
    } catch (err: any) {
      console.error('OAuth error:', err)
      setError(err.response?.data?.message || 'Failed to initiate Google OAuth.')
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img src={logo} alt="Grackle CRM" className="h-16 w-16 object-contain" />
          </div>
          <div className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold">Welcome to Grackle CRM</CardTitle>
            <CardDescription>Sign in to access your CRM dashboard</CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Primary Action: Google OAuth */}
          {!showAdminLogin && (
            <div className="space-y-4">
              <Button
                type="button"
                variant="default"
                size="lg"
                className="w-full"
                onClick={handleGoogleOAuth}
                disabled={isGoogleLoading}
                aria-label="Sign in with Google account"
              >
                {isGoogleLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                    Redirecting to Google...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign in with Google
                  </>
                )}
              </Button>

              {/* Admin Login Toggle */}
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setShowAdminLogin(true)}
                  className="text-sm text-muted-foreground"
                  aria-label="Show admin password login"
                >
                  Admin Login
                </Button>
              </div>

              {/* Help Text */}
              <div className="text-center text-sm text-muted-foreground">
                <p>Sign in with your @grackledocs.com Google account</p>
              </div>
            </div>
          )}

          {/* Admin Password Login Form */}
          {showAdminLogin && (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-password">Admin Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="Enter admin password"
                  aria-label="Admin password input"
                  autoComplete="current-password"
                />
                <p className="text-xs text-muted-foreground">For {ADMIN_EMAIL} only</p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={isLoading || !password}
                  className="flex-1"
                  aria-label="Sign in with admin password"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAdminLogin(false)
                    setPassword('')
                    setError('')
                  }}
                  aria-label="Cancel admin login"
                >
                  Cancel
                </Button>
              </div>

              {/* Back to Google Login */}
              <div className="text-center">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => {
                    setShowAdminLogin(false)
                    setPassword('')
                    setError('')
                  }}
                  className="text-sm"
                  aria-label="Back to Google sign in"
                >
                  ← Back to Google Sign In
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
